import { useState, useEffect } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useLocation } from 'react-router-dom'
import debounce from 'lodash/debounce'

import ConnectWallet from 'src/ui/molecules/ConnectWallet'
import WrappedInput from 'src/ui/atoms/Input/Wrapped'
import Button from 'src/ui/atoms/Button'
import Icon from 'src/ui/atoms/Icon'
import useIssueEvmContract from './useIssueEvmContract'
import useQueryParams from 'src/hooks/useQueryParams'
import useValidateToken from './useValidateToken'

const EvmForm = ({ chain }: { chain: string }) => {
  const [validatedToken, setValidateToken] = useState({
    isValid: false,
    data: {
      name: '',
    },
    error: '',
  })
  const { validateToken } = useValidateToken()

  const location = useLocation()
  const isVesting = location.pathname === '/issue-vesting-contract'
  const isStaking = location.pathname === '/issue-staking-contract'

  const network = 'testnet'
  const { isLoading, isWalletConnected, issueContract } = useIssueEvmContract({
    operation: isVesting ? 'create Vesting Contract' : 'create Staking Contract',
    projectType: isVesting ? 'vesting' : 'staking',
  })

  const { defaultValues, setParams } = useQueryParams({
    fields: isVesting
      ? [{ name: 'tokenAddress' }, { name: 'contractName' }]
      : [{ name: 'tokenAddress' }, { name: 'contractName' }, { name: 'apy', initialValue: 10 }],
  })

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onChange',
    defaultValues,
  })

  const watchData = useWatch({ control })

  useEffect(() => {
    setParams(watchData)
  }, [watchData])

  const handleIssue = async (data: any) => {
    await issueContract({
      ...data,
      network,
      chain,
    })
  }

  const handleValidateToken = debounce(async (value: string) => {
    const { isValid, data, error } = await validateToken({
      tokenAddress: value,
      chain,
      network,
    })

    setValidateToken({ isValid, data, error })

    if (error) {
      return error
    }
  }, 500)

  return (
    <div className="grid gap-12">
      <ConnectWallet network={network} chain={chain} />
      <form className="grid gap-4" onSubmit={handleSubmit((data) => handleIssue(data as any))}>
        <h2 className="text-white">Settings</h2>
        <WrappedInput
          placeholder="Contract name"
          label="Contract name"
          error={errors.contractName}
          {...register('contractName', {
            required: 'Required field',
          })}
        />
        <div className="flex w-full gap-4">
          <div className="flex-grow">
            <WrappedInput
              placeholder="0xabc..."
              label="Token address"
              error={errors.tokenAddress}
              info="Please enter the verified smart contract address for the token you'd like the community to stake. The smart contract for the token must be verified."
              {...register('tokenAddress', {
                required: 'Required field.',
                validate: handleValidateToken as any,
              })}
            />
          </div>

          {validatedToken.isValid && (
            <div className="flex translate-y-3 flex-row items-center gap-2">
              {validatedToken.data && (
                <span className="text-main-blue">{validatedToken.data?.name}</span>
              )}
              <Icon name="tick-circle" active />
            </div>
          )}
        </div>
        {isStaking && (
          <WrappedInput
            placeholder='e.g. "50"'
            label="APY (%)"
            error={errors.apy}
            type="number"
            {...register('apy', {
              min: 1,
            })}
          />
        )}

        <div className="ml-auto">
          <Button
            name="Create contract"
            loading={isLoading}
            disabled={!isValid || !isWalletConnected}
          />
        </div>
      </form>
    </div>
  )
}

export default EvmForm
